import * as React from "react";
import {
  Column,
  Columns,
  Stack,
  Hidden,
  Section,
} from "@origin-digital/ods-core";
import { useNavigate, useParams } from "react-router-dom";
import { logger } from "@origin-digital/reporting-client";
import { IAppContext } from "../AppContext";
import { AccordionController } from "../components/Accordion";
import { Loading } from "../components/Loading";
import { UseParamsType } from "../router";
import { useDaxi } from "../services/useDaxi";
import { ICategoryAPIResponse } from "../../../shared/interface/category";
import { ISubcategory } from "../../../shared/interface/subcategory";
import { ContentHeader } from "../components/SearchBanner/ContentHeader";
import { PathnameMap } from "../interfaces/breadcrumbs";
import {
  BreadrumbBaseName,
  BreadcrumbBaseUri,
} from "../components/Breadcrumbs/Breadcrumbs.controller";
import { IBreadcrumb } from "../../../shared/interface/breadcrumb";
import { getCategoryAPIPath } from "../services/paths";
import metaContent from "../cms/meta.json";
import { routes } from "../helpers/router.helper";
import { ScreenTemplate } from "./ScreenTemplate";

interface IProps {
  context: IAppContext;
}

export const Category = ({ context }: IProps) => {
  const { isNative } = context;
  const { category } = useParams() as UseParamsType;
  const navigate = useNavigate();

  const categoryRes = useDaxi<ICategoryAPIResponse>(
    getCategoryAPIPath(category),
    {
      xhr: true,
    }
  );

  const daxiError = categoryRes.error;

  const data = categoryRes.data;
  const categoryName = data?.category?.name || "";
  const categoryUri = data?.category?.uri || "";
  // sub categories, and sort by order
  const subCategories = (data?.subcategories || []).sort(
    (a, b) => a.order - b.order
  );
  const breadcrumbs = data?.breadcrumbs || [];
  const metadata = data?.metadata;
  const description = categoryRes.loading
    ? ""
    : metadata?.description ||
      data?.category?.description ||
      metaContent.defaultCategoryDescription;

  if (daxiError || (data !== undefined && Object.entries(data).length === 0)) {
    logger.warn(`[Category] cannot find category: "${category}"`);
    navigate(routes.home(), { replace: true });
  }

  return (
    <ScreenTemplate
      data-id="category"
      metadata={{ title: categoryName, description }}
    >
      <Section backgroundColor="transparent">
        <ContentHeader
          title={categoryName}
          description={description}
          isNative={isNative}
          hideTitleAndDescription={false}
          hideBreadcrumbs={false}
          titleIsHeading={true}
          breadcrumbsMap={BreadcrumbMap(breadcrumbs)}
        />
      </Section>
      <Section backgroundColor="white">
        <Stack space={["xlarge", "xxlarge"]}>
          <Stack space="none">
            <Columns collapseBelow="lg" space="xlarge">
              <AccordionsColumn
                subCategories={subCategories}
                categoryUri={categoryUri}
                loading={categoryRes.loading}
              />
            </Columns>
          </Stack>
        </Stack>
      </Section>
    </ScreenTemplate>
  );
};

const BreadcrumbMap = (breadcrumbs: IBreadcrumb[]): PathnameMap => {
  const breadcrumbMap = { [BreadcrumbBaseUri]: BreadrumbBaseName };
  if (breadcrumbs && breadcrumbs.length === 1) {
    breadcrumbMap[`${BreadcrumbBaseUri}/${breadcrumbs[0].uri}`] =
      breadcrumbs[0].text;
  }
  return breadcrumbMap;
};

const LoadingIndicator = () => (
  <>
    <Hidden above="md">
      <Loading size="small" />
    </Hidden>
    <Hidden below="md">
      <Loading size="medium" />
    </Hidden>
  </>
);

interface IAccordionsProps {
  subCategories: ISubcategory[];
  categoryUri: string;
  loading: boolean;
}

const AccordionsColumn = ({
  subCategories,
  categoryUri,
  loading,
}: IAccordionsProps) => {
  return (
    <Column width={loading ? "flex" : "2/3"}>
      <Stack space="none">
        {loading ? (
          <LoadingIndicator />
        ) : (
          <AccordionController
            subcategories={subCategories}
            category={categoryUri}
          />
        )}
      </Stack>
    </Column>
  );
};
